import {
  normalizeRenderSceneUpdate,
  validateRenderSceneGraph
} from '../../plugin-contracts/render_scene_contract.mjs';

const COLLECTIONS = Object.freeze(['geometries', 'styles', 'layers', 'nodes']);
const CANCELLED = Symbol('scene-request-cancelled');
const OWNER_PATTERN = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/;
const CONTEXT_FIELDS = ['projectToken', 'sheetId', 'view', 'epoch'];
const REVISION_FIELDS = ['modelRevision', 'geometryRevision', 'assetRevision'];

function fail(code, message) {
  const error = new TypeError(message);
  error.code = code;
  throw error;
}

function compareId(a, b) { return a < b ? -1 : a > b ? 1 : 0; }
function equalData(a, b) {
  if (a === b) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object'
    || Array.isArray(a) !== Array.isArray(b)) return false;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length
    && keys.every(key => Object.hasOwn(b, key) && equalData(a[key], b[key]));
}
function consumeUnexpectedPromise(value) {
  if (value && typeof value.then === 'function') Promise.resolve(value).catch(() => undefined);
}
function sameContext(a, b, includeRevisions = true) {
  if (!a || !b) return a === b;
  return CONTEXT_FIELDS.every(key => a[key] === b[key])
    && (!includeRevisions || REVISION_FIELDS.every(key => a[key] === b[key]));
}

function readContext(getContext) {
  const value = getContext();
  if (!value || !value.projectToken) return null;
  if (!REVISION_FIELDS.every(key => Number.isSafeInteger(value[key]) && value[key] >= 0)) {
    fail('SCENE_CONTEXT_INVALID', 'Scene context requires explicit nonnegative revisions.');
  }
  return Object.freeze(Object.fromEntries([...CONTEXT_FIELDS, ...REVISION_FIELDS].map(key => [key, value[key]])));
}

/** Collision-free keys; local identifiers are never interpreted as global IDs. */
export function renderSceneKey(owner, id) { return JSON.stringify([owner, id]); }

/**
 * Owns only detached neutral CPU data. No renderer, document mutation or RAF work.
 * The host calls synchronizeContext on changes; every public read/request also checks it.
 */
export function createRenderSceneService({
  getContext,
  onRelease = () => undefined,
  onError = () => undefined,
  maxProviders = 64,
  maxTotalRecords = 200_000,
  maxTotalBytes = 64 * 1024 * 1024,
  measure = (_name, work) => work(),
  requestTimeoutMs = 30_000
} = {}) {
  if (typeof getContext !== 'function') throw new TypeError('getContext is required.');
  if (![maxProviders, maxTotalRecords, maxTotalBytes, requestTimeoutMs].every(value => Number.isSafeInteger(value) && value > 0)
    || requestTimeoutMs > 120_000) throw new TypeError('Invalid scene service limits.');
  const providers = new Map();
  const recordSizes = new WeakMap();
  let context = null;
  let generation = 0;
  let requestSequence = 0;
  let disposed = false;

  function report(owner, error) {
    const issue = Object.freeze({ owner, code: typeof error?.code === 'string' ? error.code : 'SCENE_PROVIDER_FAILED' });
    try { consumeUnexpectedPromise(onError(issue)); } catch { /* Reporting cannot block other owners. */ }
    return issue.code;
  }

  function release(owner, snapshot, reason) {
    if (!snapshot) return;
    // Only identities cross this notification port; no old geometry is retained here.
    const resources = Object.freeze(Object.fromEntries(COLLECTIONS.map(key => [key,
      Object.freeze(snapshot[key].map(record => Object.freeze({ id: record.id, revision: record.revision })))
    ])));
    try { consumeUnexpectedPromise(onRelease(Object.freeze({ owner, reason, revision: snapshot.revision, resources }))); }
    catch (error) { report(owner, error); }
  }

  function cancel(record) {
    const pending = record.pending;
    record.pending = null;
    if (!pending) return;
    // Resolve the host wait even when the provider ignores AbortSignal forever.
    pending.finish(CANCELLED);
    pending.controller.abort();
  }

  function clearRecord(owner, record, reason) {
    cancel(record);
    const previous = record.snapshot;
    record.snapshot = null;
    record.snapshotContext = null;
    record.error = null;
    release(owner, previous, reason);
  }

  function synchronizeContext() {
    if (disposed) return null;
    const next = readContext(getContext);
    if (!sameContext(context, next, false)) {
      context = next;
      generation += 1;
      for (const [owner, record] of providers) clearRecord(owner, record, 'context-change');
    } else if (!sameContext(context, next)) {
      context = next;
      // Retain previous immutable data as a delta base, but never expose it as current.
      for (const record of providers.values()) cancel(record);
    }
    return context;
  }

  function registerProvider(owner, provider) {
    if (disposed || typeof owner !== 'string' || owner.length > 128 || !OWNER_PATTERN.test(owner)
      || typeof provider !== 'function' || providers.has(owner) || providers.size >= maxProviders) return false;
    providers.set(owner, { provider, snapshot: null, snapshotContext: null, pending: null, error: null });
    return true;
  }

  function unregisterProvider(owner, provider) {
    const record = providers.get(owner);
    if (!record || record.provider !== provider) return false;
    providers.delete(owner);
    clearRecord(owner, record, 'provider-removed');
    return true;
  }

  function prepareSnapshot(owner, update, previous) {
    if (update.mode === 'delta' && (!previous || update.baseRevision !== previous.revision)) {
      fail('SCENE_BASE_REVISION_MISMATCH', 'Delta does not match the committed owner scene.');
    }
    if (previous && update.revision <= previous.revision) {
      fail('SCENE_REVISION_STALE', 'Scene revisions must strictly increase.');
    }
    const graph = {};
    for (const key of COLLECTIONS) {
      const old = new Map((previous?.[key] || []).map(record => [record.id, record]));
      const next = update.mode === 'delta' ? new Map(old) : new Map();
      for (const id of update.remove?.[key] || []) {
        if (!next.delete(id)) fail('SCENE_REMOVE_UNKNOWN', `Cannot remove unknown ${key} ID.`);
      }
      for (const item of update[key]) {
        const existing = old.get(item.id);
        if (existing && (item.revision < existing.revision
          || (item.revision === existing.revision && !equalData(item, existing)))) {
          fail('SCENE_RESOURCE_REVISION_REUSED', 'Changed data requires a new record revision.');
        }
        next.set(item.id, existing?.revision === item.revision ? existing : item);
      }
      graph[key] = [...next.values()];
    }
    const count = Object.values(graph).reduce((sum, records) => sum + records.length, 0);
    let byteSize = 0;
    for (const records of Object.values(graph)) for (const item of records) {
      if (!recordSizes.has(item)) recordSizes.set(item, JSON.stringify(item).length * 2);
      byteSize += recordSizes.get(item);
    }
    let total = count;
    let totalBytes = byteSize;
    for (const [otherOwner, record] of providers) {
      if (otherOwner !== owner && record.snapshot) {
        total += record.snapshot.recordCount;
        totalBytes += record.snapshot.byteSize;
      }
    }
    if (total > maxTotalRecords || totalBytes > maxTotalBytes) {
      fail('SCENE_BUDGET_EXCEEDED', 'Combined scene data budget exceeded.');
    }
    validateRenderSceneGraph(graph);
    graph.layers.sort((a, b) => a.order - b.order || compareId(a.id, b.id));
    const layerOrder = new Map(graph.layers.map(layer => [layer.id, layer.order]));
    graph.nodes.sort((a, b) => layerOrder.get(a.layerId) - layerOrder.get(b.layerId)
      || compareId(a.layerId, b.layerId) || a.order - b.order || compareId(a.id, b.id));
    for (const key of COLLECTIONS) Object.freeze(graph[key]);
    return Object.freeze({ owner, generation, revision: update.revision, recordCount: count, byteSize, ...graph });
  }

  async function request(owner) {
    const captured = synchronizeContext();
    const record = providers.get(owner);
    if (!record) return Object.freeze({ status: 'unsupported', owner });
    if (disposed || !captured) return Object.freeze({ status: 'unavailable', owner });
    cancel(record);
    const controller = new AbortController();
    let finish;
    const cancelled = new Promise(resolve => { finish = resolve; });
    const pending = { controller, finish };
    record.pending = pending;
    const capturedGeneration = generation;
    const requestId = ++requestSequence;
    const input = Object.freeze({ owner, generation, requestId,
      modelRevision: captured.modelRevision, geometryRevision: captured.geometryRevision,
      assetRevision: captured.assetRevision, baseRevision: record.snapshot?.revision ?? null,
      signal: controller.signal });
    let timer;
    const fresh = () => {
      synchronizeContext();
      return !disposed && providers.get(owner) === record && record.pending === pending
        && generation === capturedGeneration && sameContext(captured, context);
    };
    try {
      const timeout = new Promise((_resolve, reject) => {
        timer = setTimeout(() => {
          const error = new Error('Scene provider timed out.');
          error.code = 'SCENE_REQUEST_TIMEOUT';
          reject(error);
        }, requestTimeoutMs);
      });
      // Promise.race installs rejection handlers for late/rejected provider answers too.
      const provider = record.provider;
      const operation = Promise.resolve().then(() => fresh() ? provider(input) : CANCELLED);
      const answer = await Promise.race([operation, cancelled, timeout]);
      if (answer === CANCELLED || !fresh()) return Object.freeze({ status: 'stale', owner });
      const update = measure('provider.normalize', () => normalizeRenderSceneUpdate(answer));
      const next = measure('provider.host-commit', () => prepareSnapshot(owner, update, record.snapshot));
      if (!fresh()) return Object.freeze({ status: 'stale', owner });
      const previous = record.snapshot;
      record.snapshot = next;
      record.snapshotContext = captured;
      record.error = null;
      // Notify only resources which were actually replaced/removed, after atomic commit.
      if (previous) {
        const obsolete = { revision: previous.revision };
        for (const key of COLLECTIONS) {
          const retained = new Set(next[key]);
          obsolete[key] = previous[key].filter(item => !retained.has(item));
        }
        if (COLLECTIONS.some(key => obsolete[key].length)) release(owner, obsolete, 'update');
      }
      return Object.freeze({ status: 'committed', owner, generation, revision: next.revision });
    } catch (error) {
      if (!fresh()) return Object.freeze({ status: 'stale', owner });
      record.error = report(owner, error);
      return Object.freeze({ status: 'failed', owner, code: record.error });
    } finally {
      clearTimeout(timer);
      if (record.pending === pending) record.pending = null;
      controller.abort();
    }
  }

  function getSnapshot(owner) {
    synchronizeContext();
    const record = providers.get(owner);
    return record && sameContext(context, record.snapshotContext) ? record.snapshot : null;
  }

  function status(owners) {
    synchronizeContext();
    if (!Array.isArray(owners)) throw new TypeError('Explicit visible owners are required.');
    const entries = Object.freeze([...new Set(owners)].map(owner => {
      const record = providers.get(owner);
      const state = !record ? 'unsupported' : record.pending ? 'pending' : record.error ? 'failed'
        : record.snapshot && sameContext(context, record.snapshotContext) ? 'ready' : 'unavailable';
      return Object.freeze({ owner, status: state, code: record?.error ?? null });
    }));
    return Object.freeze({ ready: !disposed && Boolean(context) && entries.every(entry => entry.status === 'ready'), entries });
  }

  function reset(reason = 'reset') {
    generation += 1;
    for (const [owner, record] of providers) clearRecord(owner, record, reason);
  }

  function releaseOwner(owner) {
    const record = providers.get(owner);
    if (!record) return false;
    clearRecord(owner, record, 'owner-release');
    return true;
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    reset('dispose');
    providers.clear();
    context = null;
  }

  return Object.freeze({ registerProvider, unregisterProvider, request, getSnapshot, status,
    synchronizeContext, reset, releaseOwner, dispose });
}
